import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Download, FileText } from "lucide-react";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import { Button } from "@/components/ui/button";
import { ReviewSettingsFields } from "@/components/review/ReviewSettingsFields";
import { getContractPublicUrl } from "@/lib/contracts";
import { reviewToFormData } from "@/lib/review-form-data";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import type { ReviewFormData, ReviewRecord, SupportingDocInput } from "@/types";

export interface EditReviewSheetProps {
  review: ReviewRecord;
  isOpen: boolean;
  onClose: () => void;
  /** Called with form data; parent shows full-screen progress and runs regenerate. */
  onRequestRegenerate: (formData: ReviewFormData) => void;
}

export function EditReviewSheet({ review, isOpen, onClose, onRequestRegenerate }: EditReviewSheetProps) {
  const [formData, setFormData] = useState<ReviewFormData>(() => reviewToFormData(review));
  const [newSupportingDocs, setNewSupportingDocs] = useState<SupportingDocInput[]>([]);

  const hasContractText =
    typeof review.contract_text === "string" && review.contract_text.trim().length > 0;

  useEffect(() => {
    if (!isOpen) return;
    setFormData(reviewToFormData(review));
    setNewSupportingDocs([]);
  }, [isOpen, review]);

  const handleDownload = useCallback(async () => {
    if (!review.contract_file_path) return;
    const downloadUrl = await getContractPublicUrl(review.contract_file_path);
    if (!downloadUrl) return;
    const link = window.document.createElement("a");
    link.href = downloadUrl;
    link.download = review.file_name || "contract.pdf";
    link.target = "_blank";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }, [review.contract_file_path, review.file_name]);

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!hasContractText || instructionsExceedLimit(formData.agentInstructions)) return;
    onRequestRegenerate({
      ...formData,
      supportingDocs: newSupportingDocs,
    });
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Edit review &amp; re-run</SheetTitle>
          <SheetDescription className={pageDescriptionMobileHiddenClassName}>
            Update background and review settings. The same contract will be analyzed again;
            discussions and report chat for this review will be cleared.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <SheetBody className="flex flex-col gap-6">
            <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                <FileText className="h-4 w-4 text-violet-400" />
                Contract (unchanged)
              </div>
              <p className="truncate text-sm text-slate-400">{review.file_name || "Contract"}</p>
              {review.contract_file_path ? (
                <Button type="button" variant="surface" size="sm" className="w-fit gap-2" onClick={handleDownload}>
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
              ) : null}
            </div>

            {!hasContractText ? (
              <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                This review has no stored contract text, so it cannot be re-run from here. Start a new review
                with the same file instead.
              </p>
            ) : null}

            <ReviewSettingsFields
              reviewId={review.id}
              formData={formData}
              onFormDataChange={setFormData}
              newSupportingDocs={newSupportingDocs}
              onNewSupportingDocsChange={setNewSupportingDocs}
              active={isOpen}
            />
          </SheetBody>

          <SheetFooter className="border-t border-white/10">
            <Button type="button" variant="surface" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasContractText || instructionsExceedLimit(formData.agentInstructions)}
            >
              Save &amp; re-run review
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
